import React from 'react'
import Helmet from 'react-helmet'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Media from 'components/common/Media'
import { H2 } from 'components/common/Text/Text'
import Mixin from 'components/common/Mixin'
import Button from 'components/common/Button/Button'
import Icon from 'components/common/Icon/Icon'
import { arrayHasValues } from 'helpers/utils'
import {
  StoreOwnerInfo,
  StoreWorkingHoursLine,
  StoreHolidays,
  StoreContactsLineA,
  StoreContactsLineText
} from '../'
import { STATES_OBJ, STORE_DETAILS_SCHEMA_ID } from 'constant'
import FleetCertified from './components/FleetCertified'

const StoreDetails = ({
  isGeolocationAvailable,
  isGeolocationEnabled,
  storeInformation: {
    owner,
    owner: {
      email = ''
    } = {},
    storeAddress = {},
    storeAddress: {
      address1 = '',
      city = '',
      state = '',
      zipcode = '',
      phoneNumber: {
        areaCode = '',
        firstThree = '',
        lastFour = ''
      } = {},
      latitude = '',
      longitude = ''
    } = {},
    appointmentEnable,
    curbsideService = false
  } = {},
  distinctWorkingHours = [],
  locationPhoneNumberFormatted,
  locationPhoneNumberUrl,
  routeToLocationUrl,
  storeSpecialAndClosedHours,
  handlePhoneLinkClick,
  storeNum,
  schemaOpeningHours = undefined,
  schemaUrl = undefined,
  openingHoursSpecification,
  siteDomain,
  handleCurbsideServiceInfo,
  isFleetCertified,
  showStoreDetailsSchema,
  hasGoldAwardBadge
}) => <React.Fragment>
  {showStoreDetailsSchema && (
    <Helmet>
      <script type="application/ld+json" id={ STORE_DETAILS_SCHEMA_ID }>
        {
          JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'TireShop',
            '@id': 'https://www.bigotires.com/',
            name: `Big O Tires ${city}`,
            url: schemaUrl,
            alternateName: 'Tire Shop',
            email,
            image: 'https://tbc.scene7.com/is/image/TBCCorporation/bot-Big-o-logo?wid=400',
            hasMap: {
              '@type': 'Map',
              mapType: 'ParkingMap',
              url: routeToLocationUrl
            },
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'US',
              addressLocality: city,
              addressRegion: state,
              postalCode: zipcode,
              streetAddress: address1
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: `${latitude}`,
              longitude: `${longitude}`
            },
            openingHoursSpecification,
            openingHours: schemaOpeningHours,
            telephone: `(${areaCode}) ${firstThree}-${lastFour}`,
            priceRange: '$$$',
            currenciesAccepted: 'USD',
            paymentAccepted: 'Cash, Credit Card',
            additionalProperty: [
              {
                '@type': 'PropertyValue',
                propertyID: 'appointments',
                value: appointmentEnable
              },
              {
                '@type': 'PropertyValue',
                propertyID: 'street',
                value: address1
              }
            ],
            containedInPlace: {
              '@type': 'Place',
              name: zipcode,
              alternateName: `${zipcode}, ${STATES_OBJ[state]}, United States`,
              containedInPlace: {
                '@type': 'City',
                name: city,
                alternateName: `${city}, ${STATES_OBJ[state]}, United States`,
                containedInPlace: {
                  '@type': 'State',
                  name: `${STATES_OBJ[state]}`,
                  alternateName: `${STATES_OBJ[state]}, United States`,
                  containedInPlace: {
                    '@type': 'Country',
                    name: 'United States',
                    alternateName: 'United States',
                    address: {
                      '@type': 'PostalAddress',
                      addressCountry: 'US'
                    }
                  },
                  address: {
                    '@type': 'PostalAddress',
                    addressCountry: 'US',
                    addressRegion: state
                  }
                },
                address: {
                  '@type': 'PostalAddress',
                  addressCountry: 'US',
                  addressLocality: city,
                  addressRegion: 'CA'
                }
              },
              url: schemaUrl,
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'US',
                addressLocality: city,
                addressRegion: state,
                postalCode: zipcode
              }
            },
            branchCode: storeNum,
            potentialAction: {
              '@type': 'ReserveAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: `${siteDomain}/appointment/quick-appointment`,
                inLanguage: 'en-US',
                actionPlatform: [
                  'http://schema.org/DesktopWebPlatform',
                  'http://schema.org/IOSPlatform',
                  'http://schema.org/AndroidPlatform'
                ]
              },
              result: {
                '@type': 'Reservation',
                name: 'Book an appointment'
              },
              name: 'Big O Tires',
              hasPOS: [],
              url: schemaUrl,
              image: 'https://tbc.scene7.com/is/image/TBCCorporation/bot-Big-o-logo?wid=400'
            }
          })
        }
      </script>
    </Helmet>
  )}

  <ShopDetailsBlock isFleetCertified={ isFleetCertified }>
    <StoreOwnerInfo owner={ owner } address={ storeAddress } />

    <Media.Mobile>
      <AwardsWrapper>
        {hasGoldAwardBadge && (
          <AwardIcon mobile asset="store-award" alt="Store Award Icon" />
        )}

        { curbsideService && (
          <CurbsideWrapper>
            <Curbside onClick={ handleCurbsideServiceInfo }>
              <TireIcon asset="tire" />
              <CurbsideText>
                Curbside Service
              </CurbsideText>
              <CurbsideTitleWrapper>
                <CurbsideTitle>
                  Available
                </CurbsideTitle>
              </CurbsideTitleWrapper>
            </Curbside>
            <InfoIcon alt="" asset="icon-info-blue-regular" onClick={ handleCurbsideServiceInfo } />
          </CurbsideWrapper>
        )}

        {isFleetCertified && <FleetCertifiedComponent mobile />}

      </AwardsWrapper>
    </Media.Mobile>

    <StoreContactBlock>
      {
        storeAddress.phoneNumber && (storeAddress.phoneNumber.firstThree != null) &&
          <React.Fragment>
            <Media.Desktop>
              <StoreContactsLineText data-at-store-phone asset={ 'phone' } value={ locationPhoneNumberFormatted } />
            </Media.Desktop>
            <Media.Mobile>
              <StoreContactsLineA
                target="_self"
                asset={ 'phone' }
                value={ locationPhoneNumberFormatted }
                url={ locationPhoneNumberUrl }
                onClick={ handlePhoneLinkClick } />
            </Media.Mobile>
          </React.Fragment>
      }
      {
        owner && owner.email &&
          <StoreContactsLineA data-at-store-email asset={ 'email-blue' } value={ owner.email } url={ `mailto:${owner.email}` } target="_self" />
      }
      <Media.Desktop>
        <AwardsWrapper>
          {hasGoldAwardBadge && (
            <AwardIcon asset="store-award" alt="Store Award Icon" />
          )}

          {isFleetCertified && <FleetCertifiedComponent />}

          { curbsideService && (
            <CurbsideWrapper>
              <Curbside onClick={ handleCurbsideServiceInfo }>
                <TireIcon asset="tire" />
                <CurbsideText>
                  Curbside Service
                </CurbsideText>
                <CurbsideTitleWrapper>
                  <CurbsideTitle>
                    Available
                  </CurbsideTitle>
                </CurbsideTitleWrapper>
              </Curbside>
              <InfoIcon alt="" asset="icon-info-blue-regular" onClick={ handleCurbsideServiceInfo } />
            </CurbsideWrapper>
          )}
        </AwardsWrapper>
      </Media.Desktop>

    </StoreContactBlock>
  </ShopDetailsBlock>
  {
    distinctWorkingHours &&
      <ShopDetailsBlock data-at-store-workinghours>
        <Media.Desktop>
          <StoreHoursTitle narrow data-at-store-workinghours-title>Hours</StoreHoursTitle>
        </Media.Desktop>
        <StoreHours showBorder={ arrayHasValues(storeSpecialAndClosedHours) }>
          {
            distinctWorkingHours.map(({ day, openingHour, closingHour }, index) =>
              <StoreWorkingHoursLine
                key={ index }
                label={ day }
                value={ openingHour && closingHour && openingHour !== 'closed' ? `${openingHour} - ${closingHour}` : 'Closed' } />
            )
          }
        </StoreHours>
        {
          arrayHasValues(storeSpecialAndClosedHours) &&
          <SpecialHours>
            {
              storeSpecialAndClosedHours.map(({ dateRange, holidayName, workingHours }, index) =>
                <StoreHolidays
                  key={ index }
                  holidayName={ holidayName }
                  date={ dateRange }
                  value={ workingHours } />)
            }
          </SpecialHours>
        }
      </ShopDetailsBlock>
  }
</React.Fragment>

StoreDetails.propTypes = {
  distinctWorkingHours: PropTypes.arrayOf(PropTypes.shape({})),
  handleCurbsideServiceInfo: PropTypes.func,
  handlePhoneLinkClick: PropTypes.func,
  hasGoldAwardBadge: PropTypes.bool,
  isFleetCertified: PropTypes.bool,
  isGeolocationAvailable: PropTypes.bool,
  isGeolocationEnabled: PropTypes.bool,
  locationPhoneNumberFormatted: PropTypes.string,
  locationPhoneNumberUrl: PropTypes.string,
  openingHoursSpecification: PropTypes.arrayOf(PropTypes.shape()),
  routeToLocationUrl: PropTypes.string,
  schemaOpeningHours: PropTypes.arrayOf(PropTypes.string),
  schemaUrl: PropTypes.string,
  showStoreDetailsSchema: PropTypes.bool,
  siteDomain: PropTypes.string,
  storeInformation: PropTypes.shape({
    appointmentEnable: PropTypes.bool,
    owner: PropTypes.shape(),
    storeAddress: PropTypes.shape()
  }),
  storeNum: PropTypes.string,
  storeSpecialAndClosedHours: PropTypes.arrayOf(PropTypes.shape())
}

export default StoreDetails

const ShopDetailsBlock = styled.div`
  ${props => props.isFleetCertified && css`
    position: relative;
  `}

  &:last-child {
    border: 0;
  }

  ${Media.desktop`
    display: block;
    vertical-align: middle;
    border-bottom: 1px solid ${Theme.colors.light_grey};
    margin: 0 32px;
  `}

  ${Media.mobile`
    padding: 15px 0;
    border-bottom: 1px solid ${Theme.colors.light_grey};
  `}
`

const StoreContactBlock = styled.div`
  padding: 20px 0;
  ${Media.mobile`
    border-top: 1px solid ${Theme.colors.light_grey};
    padding-bottom: 0;
  `}
`

const StoreHoursTitle = styled(H2)`
  text-transform: uppercase;
  ${Mixin.font.GothamBold(20)}
`

const StoreHours = styled.div`
  ${Media.desktop`
    ${props => props.showBorder && css`
      padding: 0 0 20px;
    `}
  `}

  ${Media.mobile`
    padding: 0 0 25px;
  `}
`

const SpecialHours = styled(StoreHours)`
  ${Media.desktop`
    border-top: 1px solid ${Theme.colors.light_grey};
    padding: 20px 0 0 0;
  `}
`

const CurbsideWrapper = styled.div`
  margin: 25px 0 15px;
  display: flex;

  @media (max-width: 400px) {
    width: 220px;

    && span {
      font-size: 15px;
    }
  }
`

const Curbside = styled(Button)`
  position: relative;
  border: 2px solid ${Theme.colors.red};
  padding: 8px 5px 10px;
  display: flex;
  align-items: center;
  background: white;
  color: ${Theme.colors.black_new};
  cursor: pointer;
  transform: none;

  &:hover {
    background-color: white;
    color: ${Theme.colors.black_new};
  }
`

const AwardIcon = styled(Icon)`
  width: 78px;
  height: 78px;
  margin: 10px;
  ${props => props.mobile && css`
    @media (max-width: 400px) {
      width: 70px;
      height: 70px;
    }
  `}
`

const CurbsideText = styled.span`
  margin-left: 5px;
  text-transform: uppercase;
  ${Mixin.font.GothamBold(19)};
`

const CurbsideTitleWrapper = styled.div`
  position: absolute;
  background: white;
  padding: 0 8px;
  left: 22%;
  bottom: -18%;
`

const CurbsideTitle = styled.div`
  ${Mixin.font.GothamBold(8)};
  background: ${Theme.colors.red};
  color: white;
  padding: 4px 10px 3px;
  text-transform: uppercase;
  line-height: 8px;
  letter-spacing: 3px;
`

const TireIcon = styled(Icon)`
  width: 22px;
  height: 22px;
`

const InfoIcon = styled(Icon)`
  margin-left: 5px;
  width: 17px;
  height: 17px;
  cursor: pointer;
`

const FleetCertifiedComponent = styled(FleetCertified)`
  margin-right: 10px;
  ${props => props.mobile && css`
    border: none;
  `}
`

const AwardsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: start;
  gap: 10px;
`
